import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFabCornerEntries } from '../../contexts/FabStackContext';
import { FAB_STACK_COLLAPSE_THRESHOLD, shouldCollapseFabStack } from '../../lib/fabStack';
import { FAB_MOBILE_BREAKPOINT_QUERY } from '../../lib/fabMobileVisibility';
import { mediaQueryMatches, safeMatchMedia, subscribeMediaQuery } from '../../lib/safeMatchMedia';
import useFabPracticeClearance from './useFabPracticeClearance';
import './FabMenu.css';

/**
 * FabMenu — the single fan-open, tap-based FAB menu decided in #1696
 * (2026-07-27 comment): "one menu except for coach which should always be
 * prominent". Docked at the existing top-left chat dock (#1567/#1170 —
 * PLACEMENT unchanged, only the mechanism: home/help/report-a-bug/Ask Praxis
 * used to be four independently-positioned one-offs, now they are 'menu'-
 * corner registrants (see contexts/FabStackContext.jsx) this component reads
 * and renders. It renders nothing itself — every action is delegated back to
 * the registering component's own `onClick`, so state (confirm dialogs,
 * panels, drawers) stays owned by the component that already had it.
 *
 * TAP-based, deliberately: the trigger has exactly one interaction handler
 * (onClick) and no onMouseEnter/onMouseLeave anywhere in this file — hover
 * does not exist on the phones most traffic uses (decision comment, and see
 * lib/fabMobileVisibility.js for the same reasoning elsewhere in this
 * cluster). Opens/closes on tap of the trigger, Escape, or a tap outside the
 * open panel.
 *
 * The coach FAB (CoachBubble) is a DIFFERENT corner ('coach') and is never
 * read by this component — it has no path into `entries` below, which is
 * the structural guarantee the #1696 decision asked for ("coach... stays out
 * of the menu"). See __tests__/FabMenu.test.jsx (this directory) for the
 * reddened assertion if that boundary is ever crossed.
 *
 * Collapse-at-N (#1696 checklist item): once more than
 * FAB_STACK_COLLAPSE_THRESHOLD items are simultaneously active, only the
 * first N (nearest-the-trigger, i.e. highest priority) render directly; the
 * rest fold behind a "+N more" toggle rather than growing the fan without
 * bound. All four of today's registrants (home/help/bugReport/askPraxis) fit
 * under the threshold, so this never fires in production today — it exists
 * so the *next* floating button (#1696's own stated goal) inherits real
 * behaviour instead of another one-off.
 *
 * Practice-button clearance (#3633, D-203 option a): on phones the trigger is
 * hidden (visibility, so it is neither focusable nor tappable) while an
 * element marked `data-fab-avoid` overlaps its zone. Never hidden while the
 * menu is open (the learner just chose to use it), never on /chat (top-left
 * dock, nothing to clear), never on desktop. See lib/fabClearance.js.
 */
export default function FabMenu() {
  const [open, setOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const location = useLocation();
  const isChatRoute = location.pathname.startsWith('/chat/');
  const entries = useFabCornerEntries('menu');
  const triggerRef = useRef(null);

  // Phone breakpoint, tracked the same way App.jsx's FloatingHelpButton does
  // (shared query constant, eager init, guarded for no-matchMedia renders).
  const [isPhone, setIsPhone] = useState(() => mediaQueryMatches(FAB_MOBILE_BREAKPOINT_QUERY, false));
  useEffect(() => {
    const mql = safeMatchMedia(FAB_MOBILE_BREAKPOINT_QUERY); // #4215: null -> no subscription
    if (!mql) return undefined;
    const unsubscribe = subscribeMediaQuery(mql, (e) => setIsPhone(e.matches));
    setIsPhone(mql.matches);
    return unsubscribe;
  }, []);

  const practiceCovered = useFabPracticeClearance({
    enabled: isPhone && !isChatRoute && entries.length > 0,
    triggerRef,
  });
  const hiddenForPractice = practiceCovered && !open;

  const collapsed = shouldCollapseFabStack(
    Object.fromEntries(entries.map(e => [e.id, e.priority]))
  );
  const visibleEntries = collapsed ? entries.slice(0, FAB_STACK_COLLAPSE_THRESHOLD) : entries;
  const overflowEntries = collapsed ? entries.slice(FAB_STACK_COLLAPSE_THRESHOLD) : [];

  const hasBadge = entries.some(e => e.meta?.badge);

  // Close on Escape; close (and drop the overflow sub-panel) if every
  // registrant disappears from underneath an open menu (e.g. sign-out).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (entries.length === 0) { setOpen(false); setOverflowOpen(false); }
  }, [entries.length]);

  if (entries.length === 0) return null;

  const activate = (item) => {
    setOpen(false);
    setOverflowOpen(false);
    item.meta?.onClick?.();
  };

  return (
    <>
      {open && (
        <div
          className="fab-menu-scrim"
          onClick={() => { setOpen(false); setOverflowOpen(false); }}
          data-html2canvas-ignore="true"
        />
      )}
      <div
        className={`fab-menu${isChatRoute ? ' fab-menu--chat-route' : ''}`}
        data-fab-hidden-for-practice={hiddenForPractice ? 'true' : undefined}
        data-html2canvas-ignore="true"
      >
        <button
          type="button"
          ref={triggerRef}
          id="fab-menu-trigger"
          data-testid="fab-menu-trigger"
          className={`fab-menu-trigger${hasBadge ? ' fab-menu-trigger--badge' : ''}`}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="fab-menu-panel"
          onClick={() => setOpen(o => !o)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" />
            ) : (
              <>
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </>
            )}
          </svg>
          {hasBadge && <span className="fab-menu-trigger__badge-dot" aria-hidden="true" />}
        </button>

        {open && (
          <div
            id="fab-menu-panel"
            data-testid="fab-menu-panel"
            className="fab-menu-panel"
            role="group"
            aria-label="Menu"
          >
            {visibleEntries.map((item) => (
              <FabMenuItem key={item.id} item={item} onActivate={() => activate(item)} />
            ))}
            {overflowEntries.length > 0 && !overflowOpen && (
              <button
                type="button"
                className="fab-menu-item fab-menu-item--overflow-toggle"
                data-testid="fab-menu-item-more"
                onClick={() => setOverflowOpen(true)}
              >
                +{overflowEntries.length} more
              </button>
            )}
            {overflowOpen && overflowEntries.map((item) => (
              <FabMenuItem key={item.id} item={item} onActivate={() => activate(item)} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function FabMenuItem({ item, onActivate }) {
  const { icon, label, ariaLabel } = item.meta || {};
  return (
    <button
      type="button"
      className="fab-menu-item"
      data-testid={item.meta?.testId || `fab-menu-item-${item.id}`}
      aria-label={ariaLabel || label}
      onClick={onActivate}
    >
      <span className="fab-menu-item__icon" aria-hidden="true">{icon}</span>
      <span className="fab-menu-item__label">{label}</span>
    </button>
  );
}
